import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { getClientFundsData } from "../../api/clientFunds";
import { downloadFile } from "../../api/fileDownloader";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { BrandLoader } from "../../components/Brand";
import styles from "./FundsPage.module.css";

const COLUMNS = [
  { key: "broker_name", label: "Broker Name" },
  { key: "broker_client_code", label: "Client Code" },
  { key: "client_name", label: "Client Name" },
  { key: "family_tag", label: "Family Tag" },
  { key: "available_margin", label: "Available Margin" },
  { key: "total_margin", label: "Total Margin" },
  { key: "margin_used", label: "Margin Used" },
  { key: "collateral_value", label: "Collateral Value" },
  { key: "cash_margin", label: "Cash Margin" },
  { key: "notional_cash", label: "Notional Cash" },
];

const NUMERIC_KEYS = [
  "initial_margin",
  "cash_margin",
  "collateral_value",
  "notional_cash",
  "margin_used",
  "available_margin",
  "total_margin",
];

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN");
}

function formatFetchedAt(date) {
  if (!date) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function fundsRowMatchesSearch(row, query) {
  const tokens = String(query ?? "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const parts = COLUMNS.map((col) => {
    const raw = row?.[col.key];
    if (raw == null || raw === "") return "";
    const asText = String(raw);
    const compact = asText.replace(/[\s,]/g, "");
    return `${asText} ${compact}`;
  });
  const haystack = parts.join(" ").toLowerCase();
  return tokens.every((token) => haystack.includes(token) || haystack.replace(/[\s,]/g, "").includes(token.replace(/[\s,]/g, "")));
}

function FundsPage() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await getClientFundsData();
      const list = Array.isArray(response) ? response : response?.data ?? [];
      setData(list);
      setLastFetchedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load funds data");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const filteredData = useMemo(
    () => data.filter((row) => fundsRowMatchesSearch(row, searchQuery)),
    [data, searchQuery]
  );

  const totals = useMemo(() => {
    const t = { count: filteredData.length };
    NUMERIC_KEYS.forEach((key) => {
      t[key] = filteredData.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
    });
    return t;
  }, [filteredData]);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadFile("/api/analytics/download-excel/Margin/");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchedLabel = formatFetchedAt(lastFetchedAt);

  return (
    <div className={styles["container"]}>
      <div className={styles["content-wrapper"]}>
        <div className={styles["table-section"]}>
          <div className={styles["section-header"]}>
            <div className={styles["section-header-left"]}>
              <h2 className={styles["section-title"]}>
                <GlobalIcon iconName="FundsIcon" className={styles["section-title-icon"]} />
                FUNDS
              </h2>
              {fetchedLabel ? (
                <span className={styles["fetched-at"]} title="Last funds fetch time">
                  Updated {fetchedLabel}
                </span>
              ) : null}
            </div>
            <div className={styles["section-header-actions"]}>
              <TextField
              className={styles["search-field"]}
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              variant="outlined"
              inputProps={{ "aria-label": "Search funds" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery("")}
                      className={styles["clear-search-button"]}
                      edge="end"
                    >
                      ×
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
              <Button
                variant="outlined"
                onClick={() => void fetchData()}
                className={styles["refresh-button"]}
                startIcon={
                  isLoading ? (
                    <BrandLoader variant="icon" size={16} />
                  ) : (
                    <GlobalIcon iconName="RefreshIcon" className={styles["button-icon"]} />
                  )
                }
                disabled={isLoading}
                aria-label="Refresh funds"
              >
                {isLoading ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleExport}
                className={styles["export-button"]}
                endIcon={
                  isExporting ? (
                    <BrandLoader variant="icon" size={16} />
                  ) : (
                    <GlobalIcon iconName="DownloadIcon" className={styles["button-icon"]} />
                  )
                }
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </div>
          </div>

          <TableContainer component={Paper} className={styles["table-container"]}>
            {isLoading && data.length === 0 ? (
              <div className={styles["loading-panel"]}>
                <BrandLoader variant="row" caption="Loading funds…" />
              </div>
            ) : (
              <Table className={styles["table"]} size="small" stickyHeader>
                <TableHead>
                  <TableRow className={styles["header-row"]}>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.key} className={styles["header-cell"]}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {error && data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} className={styles["empty-message-cell"]}>
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} className={styles["empty-message-cell"]}>
                        Funds not available.
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} className={styles["empty-message-cell"]}>
                        No rows match your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {filteredData.map((row, index) => (
                        <TableRow key={row.client ?? index} className={styles["data-row"]}>
                          <TableCell className={styles["broker-name-cell"]}>
                            {row.broker_name ?? "—"}
                          </TableCell>
                          <TableCell className={styles["data-cell"]}>
                            {row.broker_client_code ?? "—"}
                          </TableCell>
                          <TableCell className={styles["data-cell"]}>
                            {row.client_name ?? "—"}
                          </TableCell>
                          <TableCell className={styles["data-cell"]}>
                            {row.family_tag != null && String(row.family_tag).trim() !== ""
                              ? String(row.family_tag).trim()
                              : "—"}
                          </TableCell>
                          <TableCell className={styles["numeric-cell"]}>
                            {formatNumber(row.available_margin)}
                          </TableCell>
                          <TableCell className={styles["numeric-cell"]}>
                            {formatNumber(row.total_margin)}
                          </TableCell>
                          <TableCell className={styles["numeric-cell"]}>
                            {formatNumber(row.margin_used)}
                          </TableCell>
                          <TableCell className={styles["numeric-cell"]}>
                            {formatNumber(row.collateral_value)}
                          </TableCell>
                          <TableCell className={styles["numeric-cell"]}>
                            {formatNumber(row.cash_margin)}
                          </TableCell>
                          <TableCell className={styles["numeric-cell"]}>
                            {formatNumber(row.notional_cash)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className={styles["total-row"]}>
                        <TableCell className={styles["total-cell"]}>Total</TableCell>
                        <TableCell className={styles["total-cell"]}>{totals.count}</TableCell>
                        <TableCell className={styles["total-cell"]} />
                        <TableCell className={styles["total-cell"]} />
                        <TableCell className={styles["total-cell"]}>{formatNumber(totals.available_margin)}</TableCell>
                        <TableCell className={styles["total-cell"]}>{formatNumber(totals.total_margin)}</TableCell>
                        <TableCell className={styles["total-cell"]}>{formatNumber(totals.margin_used)}</TableCell>
                        <TableCell className={styles["total-cell"]}>{formatNumber(totals.collateral_value)}</TableCell>
                        <TableCell className={styles["total-cell"]}>{formatNumber(totals.cash_margin)}</TableCell>
                        <TableCell className={styles["total-cell"]}>{formatNumber(totals.notional_cash)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </div>
      </div>
    </div>
  );
}

export default FundsPage;
